import type { Lecture } from '@mathflat/domain/Lecture'
import type { Student } from '@mathflat/domain/Student'
import type { PlayerTime } from '@mathflat/videoplayer'
import { forEach, merge } from 'lodash-es'
import { z } from 'zod'

import type { ProblemScoring } from '~/@pages/@common/(ProblemScoring)/@trait/ProblemScoring.trait'

import type { LoginEntryPath } from '../constants/route'
import { cryptoUtils } from '../utils/cryptoUtils'
import { parseBooleanStringToBoolean } from '../utils/parsers'
import { PERSIST_KEYS_BASE } from './persistKeys'

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>
}

type ScoringData<T extends 'WORKSHEET' | 'WORKBOOK' = 'WORKSHEET' | 'WORKBOOK'> = {
  scoringId: ProblemScoring<T>['id']
  localAnswer: ProblemScoring<T>['_localAutoScoredAnswer'] | ProblemScoring<T>['_localScoring']
}

type ScoringUserAnswers = NonNullable<ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localUserAnswers']>

// 채점 id 는 학습지·시험지가 number, 교재가 `${number}-${number}` 다. 셀은 수식폼 LaTeX 이고 미입력 칸은 '' 다.
const StoredScoringUserAnswersSchema = z.array(
  z.tuple([
    z.union([z.number(), z.templateLiteral([z.number(), '-', z.number()])]),
    z.array(z.string()),
  ]),
)

// 저장값이 깨졌거나 모양이 다르면 복원만 포기한다 — 화면 진입을 막지 않도록 예외를 삼킨다.
const readStoredScoringUserAnswers = () => {
  try {
    const parsed = StoredScoringUserAnswersSchema.safeParse(
      JSON.parse(localStorage.getItem('scoringUserAnswers') ?? '[]'),
    )
    return parsed.success ? parsed.data : []
  } catch {
    return []
  }
}

interface StorageService {
  update(target: Omit<DeepPartial<this>, 'update'>): void
  clear(): void
}

/**
 * `-intro-offset` 접미사는 인트로 오프셋(가상 타임라인)이 켜진 상태에서 저장된 이어보기 값이다.
 * 접미사 없는 키의 값은 영상 실제 초, 붙은 키의 값은 인트로를 뺀 가상 초라서 서로 호환되지 않는다.
 * 같은 키를 쓰면 기존 값이 가상축으로 해석되어 인트로 길이만큼 어긋나므로 축별로 분리한다.
 */
type VideoDataKey =
  | 'lecture'
  | 'assigned-lecture'
  | 'problem-solving-video'
  | 'lecture-intro-offset'
  | 'assigned-lecture-intro-offset'
type VideoDataValue = Pick<PlayerTime, 'current'>
// type VideoDataValue = Pick<PlayerConfig, 'resolutionInfo' | 'playbackRate'> & Pick<PlayerTime, 'current'>

export const FCM_STORAGE_KEYS = {
  pendingFcmToken: 'pendingFcmToken',
  deviceId: 'deviceId',
} as const

const LOGIN_PATH_STORAGE_KEY = 'LOGIN_PATH'

export const USER_STORAGE_KEYS = {
  studentId: 'studentId',
  parentId: 'parentId',
  academyId: 'academyId',
  beforeLoginId: 'beforeLoginId',
  isInitialPassword: 'isInitialPassword',
  isPasswordChangedRequired: 'isPasswordChangedRequired',
} as const

export class LocalStorageService implements StorageService {
  user = {
    studentId: localStorage.getItem(USER_STORAGE_KEYS.studentId) as Student['id'] | null,
    parentId: localStorage.getItem(USER_STORAGE_KEYS.parentId),
    beforeLoginId: localStorage.getItem(USER_STORAGE_KEYS.beforeLoginId),
    academyId: localStorage.getItem(USER_STORAGE_KEYS.academyId),
    isInitialPassword:
      localStorage.getItem(USER_STORAGE_KEYS.isInitialPassword) === null
        ? null
        : parseBooleanStringToBoolean(localStorage.getItem(USER_STORAGE_KEYS.isInitialPassword)!),
    isPasswordChangedRequired:
      localStorage.getItem(USER_STORAGE_KEYS.isPasswordChangedRequired) === null
        ? null
        : parseBooleanStringToBoolean(
            localStorage.getItem(USER_STORAGE_KEYS.isPasswordChangedRequired)!,
          ),
  } as const

  // 현재 기기에 한번이라도 로그인했던 유저Id Set([]) : 사용 가이드 유도 팝업을 위함
  userIdsOnDevice = localStorage.getItem('userIdsOnDevice')
    ? new Set(JSON.parse(localStorage.getItem('userIdsOnDevice')!))
    : new Set([])

  scoring = localStorage.getItem('scoring')
    ? new Map<
        ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['id'],
        | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localAutoScoredAnswer']
        | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localScoring']
      >(JSON.parse(localStorage.getItem('scoring')!))
    : new Map<
        ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['id'],
        | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localAutoScoredAnswer']
        | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localScoring']
      >()

  // 다중 셀 답은 단일 답(scoring)과 값 모양이 달라 키를 분리한다 — scoring 을 문자열로 읽는 기존 경로를 깨지 않도록.
  scoringUserAnswers = new Map<ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['id'], ScoringUserAnswers>(
    readStoredScoringUserAnswers(),
  )

  /** 현재 로그인 세션의 진입 경로. 공용 기기에서 다음 사용자에게 이월되지 않도록 로그아웃(clear)과 함께 제거된다. */
  get loginPath(): LoginEntryPath | null {
    return localStorage.getItem(LOGIN_PATH_STORAGE_KEY) as LoginEntryPath | null
  }

  setLoginPath(path: LoginEntryPath) {
    localStorage.setItem(LOGIN_PATH_STORAGE_KEY, path)
  }

  get deviceId(): string | null {
    return localStorage.getItem(FCM_STORAGE_KEYS.deviceId)
  }

  get pendingFcmToken(): string | null {
    return localStorage.getItem(FCM_STORAGE_KEYS.pendingFcmToken)
  }

  setFcmDeviceInfo({
    token,
    deviceId,
    platformType,
  }: {
    token: string
    deviceId: string
    platformType: string
  }) {
    localStorage.setItem(
      FCM_STORAGE_KEYS.pendingFcmToken,
      JSON.stringify({ token, deviceId, platformType }),
    )
    localStorage.setItem(FCM_STORAGE_KEYS.deviceId, deviceId)
  }

  clearFcmDeviceInfo() {
    localStorage.removeItem(FCM_STORAGE_KEYS.pendingFcmToken)
  }

  // auth = {
  //   token: localStorage.getItem('token'),
  // } as const

  // userIdsOnDevice : 현재 기기에 처음으로 로그인 한 기기인지 확인

  isFirstUserOnDevice() {
    // 현재 사용자의 id와 스토리지의 사용자id 중 일치하는게 있는지 확인
    const base64UserId = cryptoUtils.encodeBase64(this.user.studentId as string)
    return !this.userIdsOnDevice.has(base64UserId)
  }
  // userIdsOnDevice : 동영상 유도팝업이 다시보여지지 않기 위해 storage에 현재사용자의 id를 set
  setUserIdsOnDevice() {
    const userIds = this.userIdsOnDevice.add(
      cryptoUtils.encodeBase64(this.user.studentId as string),
    )
    this.userIdsOnDevice = userIds
    localStorage.setItem('userIdsOnDevice', JSON.stringify([...userIds]))
  }
  private _videoData: Record<VideoDataKey, Map<Lecture['id'], VideoDataValue>> = {
    lecture: localStorage.getItem('VIDEO_INFO-lecture')
      ? new Map(JSON.parse(localStorage.getItem('VIDEO_INFO-lecture')!))
      : new Map(),
    'assigned-lecture': localStorage.getItem('VIDEO_INFO-assigned-lecture')
      ? new Map(JSON.parse(localStorage.getItem('VIDEO_INFO-assigned-lecture')!))
      : new Map(),
    'problem-solving-video': localStorage.getItem('VIDEO_INFO-problem-solving-video')
      ? new Map(JSON.parse(localStorage.getItem('VIDEO_INFO-problem-solving-video')!))
      : new Map(),
    'lecture-intro-offset': localStorage.getItem('VIDEO_INFO-lecture-intro-offset')
      ? new Map(JSON.parse(localStorage.getItem('VIDEO_INFO-lecture-intro-offset')!))
      : new Map(),
    'assigned-lecture-intro-offset': localStorage.getItem(
      'VIDEO_INFO-assigned-lecture-intro-offset',
    )
      ? new Map(JSON.parse(localStorage.getItem('VIDEO_INFO-assigned-lecture-intro-offset')!))
      : new Map(),
  }

  get videoData() {
    return this._videoData
  }

  setVideoData(key: VideoDataKey, videoId: Lecture['id'], value: VideoDataValue) {
    this._videoData[key].set(videoId, value)
    localStorage.setItem(`VIDEO_INFO-${key}`, JSON.stringify([...this._videoData[key].entries()]))
  }

  removeVideoData(key: VideoDataKey, videoId: Lecture['id']) {
    this._videoData[key].delete(videoId)
    localStorage.setItem(`VIDEO_INFO-${key}`, JSON.stringify([...this._videoData[key].entries()]))
  }

  updateInitialData = {
    qr: localStorage.getItem('qr') ?? 'FALSE',
  }

  device = {
    ver: localStorage.getItem('ver'),
  }

  learningProcess = {
    isVisitedConceptLearning: localStorage.getItem('isVisitedConceptLearning') || 'FALSE',
  }

  pushNotification = {
    deniedTimestamp: localStorage.getItem('pushNotificationDeniedTimestamp'),
  }

  update(target: Omit<DeepPartial<this>, 'update'>) {
    merge(this, target)
    forEach(target, (value) => {
      forEach(value, (_value, _key) => {
        localStorage.setItem(_key, _value)
      })
    })
  }

  clear() {
    // 로컬스토리지를 clear해도 남아있어야하는 스토리지 keys.
    //   PERSIST_KEYS_BASE 는 인라인 explicit logout 과 공유 (단일 owner).
    //   INTEGRATION_KEY_V1_* 는 모두 logout 시 제거 (재마이그 필요 + dev override 도 reset OK).
    const PERSIST_KEYS = PERSIST_KEYS_BASE

    const persistItems = Object.entries(localStorage)
      .filter(([key]) => PERSIST_KEYS.some((epkKey) => key.includes(epkKey)))
      .map(([key, value]) => [key, value])

    localStorage.clear()
    persistItems.forEach(([key, value]) => {
      localStorage.setItem(key, value)
    })

    this.user = {
      studentId: null,
      parentId: null,
      beforeLoginId: null,
      academyId: null,
      isInitialPassword: null,
      isPasswordChangedRequired: null,
    }
    // this.auth = { token: null }
    this.device = { ver: null }
    this.scoring = new Map<
      ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['id'],
      | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localAutoScoredAnswer']
      | ProblemScoring<'WORKSHEET' | 'WORKBOOK'>['_localScoring']
    >()
    this.scoringUserAnswers = new Map()
  }

  clearIsInitialPassword() {
    localStorage.removeItem(USER_STORAGE_KEYS.isInitialPassword)
    this.user = {
      ...this.user,
      isInitialPassword: null,
    }
  }

  clearPasswordChangeRequired() {
    localStorage.removeItem(USER_STORAGE_KEYS.isPasswordChangedRequired)
    this.user = {
      ...this.user,
      isPasswordChangedRequired: null,
    }
  }

  // 학부모 세션 정리 — 선생님앱에서 학생앱 대리 로그인 전환 시 호출
  // parentId를 명시적으로 비우지 않으면 userType getter 가 계속 PARENT 로 판정된다.
  clearParentSession() {
    localStorage.removeItem(USER_STORAGE_KEYS.parentId)
    this.user = {
      ...this.user,
      parentId: null,
    }
  }

  /** 저장소 차단(프라이빗 모드)·quota 예외가 확인 흐름을 막지 않도록 실패 시 false */
  getConfirmSkip(storageKey: string) {
    try {
      return localStorage.getItem(storageKey) === 'true'
    } catch {
      return false
    }
  }

  /** 쓰기 실패는 무시한다 — 스킵 상태는 이번 세션 동안만 유지된다 */
  setConfirmSkip(storageKey: string, next: boolean) {
    try {
      localStorage.setItem(storageKey, String(next))
    } catch {
      // noop
    }
  }

  setScoringData<T extends 'WORKSHEET' | 'WORKBOOK'>(params: ScoringData<T>) {
    this.scoring.set(params.scoringId, params.localAnswer)
    localStorage.setItem('scoring', JSON.stringify([...this.scoring.entries()]))
  }

  removeScoringData<T extends 'WORKSHEET' | 'WORKBOOK'>(params: Pick<ScoringData<T>, 'scoringId'>) {
    this.scoring.delete(params.scoringId)
    localStorage.setItem('scoring', JSON.stringify([...this.scoring.entries()]))
    this.removeScoringUserAnswers(params)
  }

  setScoringUserAnswers<T extends 'WORKSHEET' | 'WORKBOOK'>(params: {
    scoringId: ProblemScoring<T>['id']
    userAnswers: ScoringUserAnswers
  }) {
    this.scoringUserAnswers.set(params.scoringId, params.userAnswers)
    this.persistScoringUserAnswers()
  }

  // 저장 공간 부족 등으로 쓰기가 실패해도 답 입력을 막지 않는다 — 메모리 답은 남고 새로고침 복원만 잃는다.
  private persistScoringUserAnswers() {
    try {
      localStorage.setItem(
        'scoringUserAnswers',
        JSON.stringify([...this.scoringUserAnswers.entries()]),
      )
    } catch {
      // noop
    }
  }

  removeScoringUserAnswers<T extends 'WORKSHEET' | 'WORKBOOK'>(
    params: Pick<ScoringData<T>, 'scoringId'>,
  ) {
    if (!this.scoringUserAnswers.delete(params.scoringId)) return
    this.persistScoringUserAnswers()
  }
}

// class SessionStorageService implements StorageService {
//   auth = {
//     token: sessionStorage.getItem('token'),
//   } as const

//   update(target: Omit<DeepPartial<this>, 'update'>) {
//     merge(this, target)
//     forEach(target, (value, key) => {
//       forEach(value, (_value, _key) => {
//         sessionStorage.setItem(_key, _value)
//       })
//     })
//   }

//   clear() {
//     sessionStorage.clear()
//     this.auth = { token: null }
//   }
// }

export const localStorageService = new LocalStorageService()
